import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Skeleton } from "@/components/ui/skeleton";
import { MappingsForm } from "@/components/journal/MappingsForm";
import { useActiveCompany } from "@/lib/company-context";
import { useJournalMappings, useUpdateMappings } from "@/lib/api/journal.queries";

export const Route = createFileRoute(
  "/companies/$companyId/contabilidad/cuentas",
)({
  component: CuentasPage,
});

function CuentasPage() {
  return (
    <RequireAuth>
      <AppShell>
        <CuentasContent />
      </AppShell>
    </RequireAuth>
  );
}

function CuentasContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const { data, isLoading, isError } = useJournalMappings(companyId);
  const { mutateAsync: updateMapping, isPending } = useUpdateMappings(companyId);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Settings className="h-5 w-5 text-muted-foreground" />
          {t("nav.contabilidad.cuentas")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("journal.cuentas.subtitle")}
        </p>
      </div>

      {isError ? (
        <ErrorState title={t("journal.error.loadMappings")} />
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <MappingsForm
          mappings={data?.mappings ?? []}
          onUpdate={(key, accountCode) => updateMapping({ key, accountCode })}
          isUpdating={isPending}
        />
      )}
    </div>
  );
}
